import { useCallback, useContext, useEffect, useReducer, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

import ActionButton from "../components/formInputs/buttons/ActionButton.jsx";
import BreadCrumb from "../components/breadCrumb/BreadCrumb.jsx";
import DcsdDialog from "../components/modals/DcsdDialog.jsx";
import DcsdFormModal from "../components/modals/DcsdFormModal.jsx";
import FormReducer from "../utils/FormReducer.jsx";
import GradeBlock from "../components/gradeLevelBlock/GradeBlock.jsx";
import GuardianAddressBlock from "../components/addressBlock/GuardianAddressBlock.jsx";
import Header from "../components/header/Header.jsx";
import InformationIncorrectListForm from "../components/formComponents/modalForms/InformationIncorrectListForm.jsx";
import LoadingSvg from "../components/loadingSvg/LoadingSvg.jsx";
import NoEscape from "../utils/NoEscape.jsx";
import OpenEnrollmentDao from "../dao/OpenEnrollmentDao.jsx";
import RbA from "../components/rba/RbA.jsx";
import ReasonsBlock from "../components/reasonsBlock/ReasonsBlock.jsx";
import SaveBlock from "../components/saveBlock/SaveBlock.jsx";
import StudentAddressBlock from "../components/addressBlock/StudentAddressBlock.jsx";
import StudentForm from "../components/formComponents/forms/StudentForm.jsx";
import StudentInfoDao from "../dao/StudentInfoDao.jsx";
import T from "../components/translations/Translations.jsx";
import Toolbar from "../components/Toolbar.jsx";
import UserDao from "../dao/UserDao.jsx";
import ValidationPatterns from "../utils/ValidationPatterns.jsx";
import WindowAndPermissionDialog from "../components/modals/WindowAndPermissionDialog.jsx";

import { getTodayWithTime, getEpoch } from "../utils/DateFormatter.jsx";
import { gradeTranslator, nextGrade, numberGradeToString } from "../const/Translations.jsx";
import { GlobalContext } from "../components/contextProvider/ContextProvider.jsx";
import { GUARDIAN_DASHBOARD } from "../utils/auth/config.js";

import "../styles/Guardian_StudentForm.scss";

/**
 * This will render the form and components for showing an existing Open Enrollment record.
 * Existing Guardian + Existing Student (IC data)
 * @name StudentNumber
 * @return {JSX.Element}
 */
const StudentNumber = () => {
    const { number } = useParams();

    const { dispatch, state } = useContext(GlobalContext);
    const { adwCalled, oeActiveDistrictWindow, oeWindows, studentNumber, token, userDetails } = state || {};

    const navigate = useNavigate();

    const initialFormState = {
        addressLine1: "",
        addressLine2: "",
        birthdate: "",
        city: "",
        firstName: "",
        gender: "",
        gradeApplying: "",
        gradeName: "",
        lastName: "",
        middleName: "",
        reasonsForLeaving: [], // pushing data here from ReasonsBlock
        schoolYearKey: null,
        state: "",
        studentNumber: null,
        zip: ""
    };
    const allowedRolesArray = ["EXTERNAL_GUARDIAN", "GUARDIAN"];

    const [formState, formDispatch] = useReducer(FormReducer, initialFormState);

    const [day, setDay] = useState("");
    const [enableButton, setEnableButton] = useState(false);
    const [extensionWindow, setExtensionWindow] = useState(false);
    const [guardianInfo, setGuardianInfo] = useState(null);
    const [hasReasons, setHasReasons] = useState(false);
    const [loader, setLoader] = useState(false);
    const [month, setMonth] = useState("");
    const [oeReasons, setOeReasons] = useState(null);
    const [oeReasonsFlag, setOeReasonsFlag] = useState(true);
    const [open, setOpen] = useState("false");
    const [outOfBounds, setOutOfBounds] = useState(false);
    const [preSchool, setPreSchool] = useState(false);
    const [schoolYearRange, setSchoolYearRange] = useState(null); // used to show 2004 - 2005 range
    const [sorryMessage, setSorryMessage] = useState("");
    const [sorryTitle, setSorryTitle] = useState("");
    const [studentInfoGate, setStudentInfoGate] = useState(false);
    const [studentOeData, setStudentOeData] = useState(null);
    const [studentIcData, setStudentIcData] = useState(null);
    const [validatedBirthdayDto, setValidatedBirthdayDto] = useState(null);
    const [year, setYear] = useState("");

    const isNewStudent = true;
    const oeButtonsCancel = T({ key: "OE_Buttons_Cancel" });
    const oeButtonsDelete = T({ key: "OE_Buttons_Delete" });
    const oeButtonsBackToDashboard = T({ key: "OE_Buttons_Back_To_Dashboard" });
    const oeDialogDeleteTitle = T({ key: "OE_Dialog_Delete_Title" });
    const oeDialogDeleteBody1 = T({ key: "OE_Dialog_Delete_Body1" });
    const oeDialogDeleteBody2 = T({ key: "OE_Dialog_Delete_Body2" });

    const toastDataError = T({ key: "OE_Toast_Data_Error" });
    const toastStudentSuccess = T({ key: "OE_Toast_Student_Updated_2" });
    const toastSavedStudent = T({ key: "OE_Toast_OE_Saved" });
    const toastDeleted = T({ key: "OE_Toast_Deleted" });
    const toastDeleteError = T({ key: "OE_Toast_Delete_Error" });

    const dialogClosedTitle = T({ key: "OE_Dialog_Window_Closed_Title" });
    const dialogClosedBody = T({ key: "OE_Dialog_Window_Closed_Body" });
    const dialogAccessDeniedTitle = T({ key: "OE_Dialog_Access_Denied_Title" });
    const dialogAccessDeniedBody = T({ key: "OE_Dialog_Access_Denied_Body" });
    const dialogIneligibleTitle = T({ key: "OE_Dialog_Ineligible_Title" });
    const dialogIneligibleBody = T({ key: "OE_Dialog_Ineligible_Body" });
    const dialogProcessing = T({ key: "OE_Dialog_Processing" });
    const dialogIncorrectTitle = T({ key: "OE_Dialog_Incorrect_Info_Title" });

    /**
     * NoEscape prevents the escape key from closing modals
     */
    NoEscape();

    const nextGradeToString = (grade) => {
        if (formState) {
            if (["PRE", "K"].includes(gradeTranslator(grade))) {
                return nextGrade(gradeTranslator(grade));
            }
            const number = parseInt(grade, 10) + 1;
            const padNumber = number.toString().padStart(2, "0");

            return padNumber;
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "zip") {
            if (!ValidationPatterns.digitOnly.test(value) && value !== "") {
                return false;
            }
        }
        formDispatch({
            type: "text",
            field: name,
            payload: value
        });
    };

    const handleDelete = () => {
        const options = {
            action: "oeByStudentKeyDelete",
            key: studentOeData?.studentDto?.key,
            token
        };
        setLoader(true);
        OpenEnrollmentDao(options)
            .then((response) => {
                if (response) {
                    const { errors } = response.data;
                    if (errors && errors?.length > 0) {
                        let errorMessage = `${toastDeleteError}:\n${errors.join("\n")}`;
                        toast.error(`${errorMessage}`, {
                            autoClose: false,
                            closeOnClick: true,
                            style: { width: "100%", whiteSpace: "break-spaces" }
                        });
                        setOpen("false");

                        return false;
                    }
                    toast.success(toastDeleted, {
                        autoClose: false
                    });
                    setOpen("false");
                    setTimeout(() => {
                        sessionStorage.clear();
                        window.location.replace(GUARDIAN_DASHBOARD);
                    }, 3000);

                    return true;
                }
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setLoader(false);
            });
    };

    const getDeleteActions = () => {
        return (
            <>
                <ActionButton
                    className="action-button-cancel"
                    label={oeButtonsCancel}
                    onClick={() => {
                        setOpen("false");
                    }}
                />
                <ActionButton
                    className="action-button-delete"
                    disable={loader}
                    label={oeButtonsDelete}
                    onClick={() => {
                        handleDelete();
                    }}
                />
            </>
        );
    };

    const getStudentData = useCallback(() => {
        return {
            address: {
                addressLine1: formState.addressLine1,
                addressLine2: formState.addressLine2,
                city: formState.city,
                state: formState.state,
                zip: formState.zip
            },
            birthdate: formState.birthdate,
            birthMonth: month,
            birthDay: day,
            birthYear: year,
            feederLocationKey: null,
            firstName: formState?.firstName,
            gender: formState.gender,
            gradeApplying: numberGradeToString(formState.gradeApplying),
            gradeName: numberGradeToString(formState.gradeName),
            gridCode: null,
            key: null,
            lastName: formState?.lastName,
            locationKey: null,
            middleName: formState?.middleName,
            oodSchoolName: null,
            oodSchoolCity: null,
            oodSchoolState: null,
            overrideStatus: "DEFAULT_LOCATION_CHECK",
            parentGuid: userDetails.uid,
            personId: studentIcData ? studentIcData.key : "",
            schoolYearKey: oeActiveDistrictWindow?.schoolYearKey,
            studentNumber: studentNumber
        };
    }, [day, formState, month, oeActiveDistrictWindow, studentIcData, studentNumber, userDetails, year]);

    /**
     * First: need to POST a new student (if noStudentOeData)
     *  Then: need to Get that student by key
     *  Then submit
     */
    const saveIcDataToOeData = () => {
        if (token && !studentOeData) {
            const newStudentData = getStudentData();
            const options = {
                action: "oeNewStudentPost",
                data: newStudentData,
                token
            };
            setLoader(true);
            // **** POST IC DATA TO OE FOR THE FIRST TIME **********************************************************
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { errors, payload } = response.data;
                        if (payload && payload !== null) {
                            /**
                             * If saved to OE data table is good, then use the response key
                             * to GET the student info from OE below.
                             * This is needed (for submission) after the initial POST to OE
                             */
                            if (payload) {
                                const options2 = {
                                    action: "oeByStudentKeyRead",
                                    key: payload.key,
                                    token
                                };
                                // **** GET STUDENT DATA FROM OE *******************************************************
                                OpenEnrollmentDao(options2)
                                    .then((response2) => {
                                        if (response2) {
                                            if (response2.data.payload) {
                                                const tempDto = response2.data.payload;
                                                tempDto.reasonsForLeaving = formState.reasonsForLeaving;

                                                tempDto.reasonsForLeaving.forEach((item) => {
                                                    item.locationKey = tempDto.studentDto.locationKey;
                                                    item.studentId = tempDto.studentDto.key;
                                                });

                                                const options3 = {
                                                    action: "existingStudentPut",
                                                    data: tempDto,
                                                    key: tempDto.studentDto.key,
                                                    token
                                                };
                                                // **** PUT ****************************************************************
                                                OpenEnrollmentDao(options3)
                                                    .then((response3) => {
                                                        if (response3) {
                                                            const { studentDto } = response3.data.payload;
                                                            const { key } = studentDto;

                                                            toast.success(toastSavedStudent, { autoClose: 3000 });
                                                            setTimeout(() => {
                                                                navigate(`/school-choice/${key}`);
                                                            }, 3000);
                                                            const { errors } = response3.data;
                                                            if (errors) {
                                                                toast.error(errors[0], { autoClose: false });
                                                            }
                                                            setStudentOeData(response3.data.payload);
                                                        }
                                                    })
                                                    .catch((error) => {
                                                        console.error(error);
                                                    });
                                            }
                                        }
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                    });
                            }
                        } else if (errors && errors.length > 0) {
                            setEnableButton(false);
                            toast.error(errors[0], { autoClose: false });
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    };

    const handleSubmit = () => {
        if (!studentOeData) {
            // If its the first time on Open Enrollment then we need to POST to OE. See below method.
            saveIcDataToOeData();
        } else {
            // else then copy OE data and change the reasonsForLeaving
            const newStudentData = studentOeData;
            newStudentData.reasonsForLeaving = formState.reasonsForLeaving;
            newStudentData.studentDto.gradeApplying = formState.gradeApplying;
            const options = {
                action: "existingStudentPut",
                data: newStudentData,
                key: studentOeData?.studentDto?.key,
                token
            };
            setLoader(true);
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { payload } = response.data;
                        if (payload) {
                            toast.success(toastStudentSuccess, { autoClose: 3000 });
                            setTimeout(() => {
                                navigate(`/school-choice/${payload.studentDto?.key}`);
                            }, 3000);
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    };

    const showGradeBlock = () => {
        return formState && formState.gradeName?.length > 0 && formState.gradeApplying?.length > 0 && schoolYearRange;
    };

    /**
     * Get the list of reasons to leave or attend a school
     */
    useEffect(() => {
        if (oeReasonsFlag && token && !oeReasons) {
            setOeReasonsFlag(false);
            const options = {
                action: "oeGetReasons",
                token
            };
            setLoader(true);
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { errors, payload } = response.data;
                        if (payload) {
                            const { payload } = response.data;
                            payload.sort((a, b) => {
                                return a.questionText > b.questionText ? 1 : -1;
                            });
                            const other = payload.filter((obj) => obj.questionText.toLowerCase() === "other");
                            if (other && other.length > 0) {
                                const match = payload.filter((obj) => obj.questionText.toLowerCase() !== "other");
                                match.push(other[0]);

                                setOeReasons(match);
                            }
                        } else {
                            toast.error(errors[0]);
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [oeReasons, oeReasonsFlag, token]);

    /**
     * Get IC guardian information (infinite campus)
     */
    useEffect(() => {
        if (token && userDetails && !guardianInfo) {
            const { roleDtos } = userDetails;
            const match = roleDtos.filter((obj) => obj.role === "GUARDIAN");
            if (match && match.length > 0) {
                const options = {
                    action: "guardianInfoRead",
                    username: userDetails?.username,
                    token
                };
                StudentInfoDao(options)
                    .then((response) => {
                        if (response) {
                            const { errors, payload } = response.data;
                            if (payload) {
                                setGuardianInfo(payload);
                            } else if (errors && errors.length > 0) {
                                setGuardianInfo(null);
                                toast.error(toastDataError);
                            }
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    });
            } else {
                setGuardianInfo(userDetails);
            }
        }
    }, [guardianInfo, toastDataError, token, userDetails]);

    /**
     * If no active district window, close the window
     */
    useEffect(() => {
        if (adwCalled && !oeActiveDistrictWindow) {
            setOutOfBounds(true);
            setSorryTitle(dialogClosedTitle);
            setSorryMessage(dialogClosedBody);
            setOpen("out-of-bounds");
        }
    }, [adwCalled, dialogClosedBody, dialogClosedTitle, oeActiveDistrictWindow]);

    /**
     * Get the school year range from DB
     * Used in Edit page in GradeLevelBlock area to display range (ex: 2025 - 2026)
     */
    useEffect(() => {
        if (token && oeActiveDistrictWindow && !schoolYearRange) {
            const nextYear = parseInt(oeActiveDistrictWindow.schoolYearKey, 10) + 1;
            const options = {
                action: "userSchoolYearRange",
                schoolYearKey: nextYear,
                token
            };
            setLoader(true);
            UserDao(options)
                .then((response) => {
                    if (response) {
                        const { payload } = response.data;
                        if (payload) {
                            setSchoolYearRange(payload);
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [oeActiveDistrictWindow, schoolYearRange, token]);

    /**
     * If not Parent Request, shut it down
     */
    useEffect(() => {
        if (adwCalled && oeWindows) {
            if (!oeWindows.isParentRequestAllowed && !oeWindows.isParentRequestRound2Allowed) {
                setOutOfBounds(true);
                setSorryTitle(dialogClosedTitle);
                setSorryMessage(dialogClosedBody);
                setOpen("out-of-bounds");
            }
        }
    }, [adwCalled, dialogClosedBody, dialogClosedTitle, oeWindows]);

    /**
     * Dont need to make this call if existing student
     */
    useEffect(() => {
        if (token && formState?.birthdate?.length > 0 && !validatedBirthdayDto && !isNewStudent) {
            // If the student is new then dont need to make this call. !isNewStudent defaults to true
            const options = {
                action: "oeStudentBirthdate",
                params: {
                    birthDate: formState.birthdate
                },
                token
            };
            setLoader(true);
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { errors, payload } = response.data;
                        if (payload) {
                            setValidatedBirthdayDto(payload);
                        } else {
                            setValidatedBirthdayDto(errors[0]);
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [formState, isNewStudent, token, validatedBirthdayDto]);

    /**
     * Check OE table data, if no OE data, go get IC data
     */
    useEffect(() => {
        if (oeActiveDistrictWindow && studentNumber && token && !studentIcData) {
            // studentNumber
            const options = {
                action: "studentInfoByNumberGet",
                studentNumber,
                token
            };
            setLoader(true);
            StudentInfoDao(options)
                .then((response) => {
                    if (response) {
                        const { payload } = response.data;
                        setStudentIcData(payload);
                    } else if (studentNumber && !studentIcData) {
                        setOutOfBounds(true);
                        setSorryMessage(dialogAccessDeniedBody);
                        setSorryTitle(dialogAccessDeniedTitle);
                        setOpen("out-of-bounds");
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [
        dialogAccessDeniedBody,
        dialogAccessDeniedTitle,
        number,
        oeActiveDistrictWindow,
        studentIcData,
        studentNumber,
        token
    ]);

    /**
     * Get studentKey from Open Enrollment DB
     * if we have OE data.. use that
     * else go get IC data... and use that
     */
    useEffect(() => {
        if (number && oeActiveDistrictWindow && token && !studentOeData) {
            // studentKey
            const options = {
                action: "oeByStudentNumberRead",
                schoolYearKey: oeActiveDistrictWindow.schoolYearKey,
                params: {
                    studentNumber: number
                },
                token
            };
            setLoader(true);
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { payload } = response.data;
                        if (payload && payload.length > 0) {
                            // call the detailed studentInfo api for larger DTO
                            const options = {
                                action: "oeByStudentKeyRead",
                                key: payload[0].key,
                                token
                            };
                            OpenEnrollmentDao(options)
                                .then((response) => {
                                    if (response) {
                                        const { payload } = response.data;
                                        if (payload) {
                                            setStudentOeData(payload);
                                            setStudentInfoGate(true);
                                        }
                                    }
                                })
                                .catch((error) => {
                                    console.error(error);
                                });
                        } else {
                            const options = {
                                action: "studentInfoByNumberGet",
                                studentNumber,
                                token
                            };
                            StudentInfoDao(options)
                                .then((response) => {
                                    if (response) {
                                        const { payload } = response.data;
                                        setStudentIcData(payload);
                                    }
                                })
                                .catch((error) => {
                                    console.error(error);
                                });
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [number, oeActiveDistrictWindow, studentNumber, studentOeData, token]);

    /**
     * Pluck out the month, day, year from birthdate
     */
    useEffect(() => {
        if (formState.birthdate) {
            const parsedBirthday = formState.birthdate ? formState.birthdate.split("-") : "";

            setMonth(parsedBirthday[1]);
            setDay(parsedBirthday[2]);
            setYear(parsedBirthday[0]);
        }
    }, [day, formState, month, year]);

    /**
     * Check if the student's age is within the range for open enrollment
     * If not, open the out-of-bounds dialog
     */
    useEffect(() => {
        if (day && month && schoolYearRange && year) {
            // Age 5 in milliseconds
            const baseAge = 60 * 60 * 24 * 365.242199 * 5 * 1000;
            // Grab the start year of the OE schoolYear Range (schoolYearRange is this year + 1)
            const endYear = schoolYearRange.name?.trim().slice(0, 4);
            // convert to milliseconds
            const endRange = getEpoch(`${endYear}-10-02`);
            // convert the student's birthday to milliseconds
            const ageEpoch = getEpoch(`${year}-${month}-${day}`);
            // difference between <endYear>-10-02 and student's birthday
            const timeDiff = endRange - ageEpoch;
            if (timeDiff < baseAge) {
                setSorryTitle(dialogIneligibleTitle);
                setSorryMessage(dialogIneligibleBody);
                setOpen("out-of-bounds");
            }
        }
    }, [day, dialogIneligibleBody, dialogIneligibleTitle, month, schoolYearRange, year]);

    /**
     * Check if the Open Enrollment window is open or closed
     * If outOfBounds === false, then the window is open
     */
    useEffect(() => {
        if (oeActiveDistrictWindow && !outOfBounds) {
            const { endDate, startDate } = oeActiveDistrictWindow;
            const today = getTodayWithTime();
            if (getEpoch(endDate) > getEpoch(today) && getEpoch(startDate) < getEpoch(today)) {
                setOutOfBounds(false);
            } else {
                setOutOfBounds(true);
                setSorryTitle(dialogClosedTitle);
                setSorryMessage(dialogClosedBody);
                setOpen("out-of-bounds");
            }
        }
    }, [dialogClosedBody, dialogClosedTitle, oeActiveDistrictWindow, outOfBounds]);

    /**
     * Check to make sure they have reasonsForLeaving before they can save
     */
    useEffect(() => {
        if (oeReasons && formState.reasonsForLeaving.length > 0) {
            setHasReasons(true);
            const { reasonsForLeaving } = formState;
            const otherReason = oeReasons.filter((obj) => obj.questionText.toLowerCase() === "other");
            if (otherReason && otherReason.length > 0) {
                const other = reasonsForLeaving.filter(
                    (obj) => parseInt(obj.questionId, 10) === parseInt(otherReason[0].key, 10)
                );
                if (other && other.length > 0) {
                    if (other[0].comments?.length < 1) {
                        // if the user has selected "Other" as a reasons for leaving, make sure they entered a comment
                        setHasReasons(false);
                    }
                }
            }
        } else {
            setHasReasons(false);
        }
    }, [formState, oeReasons]);

    /**
     * This useEffect is checking the formState for validation.
     * if any of the fields below are not filled out, the save button will not be active
     */
    useEffect(() => {
        if (studentNumber) {
            setEnableButton(true);
        }
        if (
            formState.zip &&
            formState.birthdate &&
            formState.city?.trim().length > 0 &&
            formState.city &&
            formState.firstName?.trim().length > 0 &&
            formState.gender?.trim().length &&
            formState.lastName?.trim().length > 0 &&
            formState.state?.trim().length > 0 &&
            formState.zip?.trim().length > 0 &&
            gradeTranslator(formState.gradeName) !== "-" &&
            gradeTranslator(formState.gradeApplying) !== "-"
        ) {
            setEnableButton(true);
        } else {
            setEnableButton(false);
        }
    }, [formState, studentNumber]);

    /**
     * Dispatch params
     */
    useEffect(() => {
        if (number && !studentNumber) {
            dispatch({
                type: "StudentNumber",
                studentNumber: number
            });
        }
    }, [dispatch, number, studentNumber]);

    /**
     * Send values to formState
     */
    useEffect(() => {
        if (oeActiveDistrictWindow && userDetails && (studentOeData || studentIcData)) {
            const updatedFormState = structuredClone(formState);

            if (studentOeData) {
                const { reasonsForLeaving, studentDto } = studentOeData;
                const { address, birthdate, firstName, gender, gradeApplying, gradeName, lastName, middleName } =
                    studentDto;
                updatedFormState.addressLine1 = address?.addressLine1?.trim();
                updatedFormState.addressLine2 = address?.addressLine2;
                updatedFormState.birthdate = birthdate?.split("T")[0];
                updatedFormState.city = address?.city;
                updatedFormState.firstName = firstName?.trim();
                updatedFormState.gender = gender;
                updatedFormState.gradeApplying = gradeApplying;
                updatedFormState.gradeName = gradeName;
                updatedFormState.lastName = lastName?.trim();
                updatedFormState.middleName = middleName?.trim();
                updatedFormState.reasonsForLeaving = reasonsForLeaving;
                updatedFormState.state = address?.state;
                updatedFormState.zip = address?.zip;
            } else {
                const {
                    address,
                    birthdate,
                    firstName,
                    gender,
                    gradeName,
                    guardians,
                    lastName,
                    middleName,
                    schoolName,
                    schoolYearName
                } = studentIcData;
                updatedFormState.addressLine1 = address?.addressLine1?.trim();
                updatedFormState.addressLine2 = address?.addressLine2?.trim();
                updatedFormState.birthdate = birthdate.split("T")[0];
                updatedFormState.city = address?.city;
                updatedFormState.firstName = firstName?.trim();
                updatedFormState.gender = gender;
                updatedFormState.gradeApplying = extensionWindow
                    ? gradeTranslator(studentIcData.gradeName)
                    : nextGradeToString(studentIcData?.gradeName);
                updatedFormState.gradeName = gradeTranslator(gradeName);
                updatedFormState.guardians = guardians;
                updatedFormState.lastName = lastName?.trim();
                updatedFormState.middleName = middleName?.trim();
                updatedFormState.reasonsForLeaving = [];
                updatedFormState.schoolName = schoolName;
                updatedFormState.schoolYearName = schoolYearName;
                updatedFormState.state = address?.state;
                updatedFormState.zip = address?.zip;
            }

            formDispatch({
                type: "reset",
                payload: { ...updatedFormState }
            });
        }
        // eslint-disable-next-line
    }, [extensionWindow, preSchool, studentIcData, studentOeData, userDetails]);

    useEffect(() => {
        if (oeWindows) {
            setExtensionWindow(oeWindows.isSchoolYearExtensionWindowAllowed);
        }
    }, [oeWindows]);

    /**
     * Dont show the reasons if the grade applying is PRE or K
     */
    useEffect(() => {
        if (!preSchool) {
            const { gradeApplying } = formState;
            if (["PRE", "K"].includes(gradeApplying)) {
                setPreSchool(true);
            }
        }
    }, [formState, preSchool]);

    return (
        <RbA allowedRoles={allowedRolesArray} redirect="/notFound">
            <Header />
            <Toolbar />
            <BreadCrumb label={`Open Enrollment > ${T({ key: "OE_Student_Info_Breadcrumb" })}`} />
            <div className="gutter-90">
                {!outOfBounds && (studentIcData || studentOeData) && (
                    <>
                        <div className="address-block-container">
                            {guardianInfo && <GuardianAddressBlock guardianInfo={guardianInfo} />}
                            {studentNumber && <StudentAddressBlock studentIcData={studentIcData} />}
                        </div>
                        <div className="mt-5 mb-3">
                            <hr />
                        </div>
                        <StudentForm
                            formState={formState}
                            formDispatch={formDispatch}
                            handleChange={handleChange}
                            studentIcData={studentIcData}
                        />
                        {showGradeBlock() && (
                            <GradeBlock
                                formDispatch={formDispatch}
                                formState={formState}
                                schoolYearRange={schoolYearRange}
                                studentIcData={studentIcData}
                            />
                        )}
                        {oeReasons && studentInfoGate && !preSchool && (
                            <ReasonsBlock
                                formDispatch={formDispatch}
                                formState={formState}
                                oeReasons={oeReasons}
                                studentIcData={studentIcData}
                                studentOeData={studentOeData}
                                studentNumber={studentNumber}
                            />
                        )}
                        {oeActiveDistrictWindow && (
                            <SaveBlock
                                enableButton={enableButton}
                                formState={formState}
                                handleSubmit={handleSubmit}
                                hasReasons={hasReasons}
                                includeDelete={studentOeData !== null}
                                open={open}
                                preSchool={preSchool}
                                setOpen={setOpen}
                                setStudentInfoGate={setStudentInfoGate}
                                studentInfoGate={studentInfoGate}
                                studentNumber={studentNumber}
                            />
                        )}
                        <ToastContainer style={{ width: "50%" }} />
                        <DcsdFormModal
                            ariaLabel="Popup modal listing 1-3 ways fix students Open Enrollment Record"
                            buttonCount="1"
                            button1Label={oeButtonsBackToDashboard}
                            id="looksIncorrect"
                            title={dialogIncorrectTitle}
                            open={open}
                            setOpen={setOpen}
                        >
                            <InformationIncorrectListForm formState={formState} />
                        </DcsdFormModal>
                        {studentOeData && (
                            <DcsdDialog
                                actions={getDeleteActions()}
                                ariaLabel="Delete Open Enrollment Confirmation Dialog"
                                hasCloseX
                                id="delete"
                                onHide={() => {
                                    setOpen("false");
                                }}
                                open={open}
                                title={oeDialogDeleteTitle}
                            >
                                <p>
                                    {oeDialogDeleteBody1}{" "}
                                    <span>
                                        {studentOeData?.studentDto?.firstName} {studentOeData?.studentDto?.lastName}
                                    </span>
                                    . {oeDialogDeleteBody2}{" "}
                                </p>
                                {loader && (
                                    <div className="dialog-loader">
                                        {dialogProcessing} <LoadingSvg />
                                    </div>
                                )}
                            </DcsdDialog>
                        )}
                    </>
                )}
                {loader && <LoadingSvg />}
                <WindowAndPermissionDialog
                    id="out-of-bounds"
                    open={open}
                    setOpen={setOpen}
                    sorryTitle={sorryTitle}
                    sorryMessage={sorryMessage}
                />
            </div>
        </RbA>
    );
};

export default StudentNumber;
